/** 金额输入只接受普通十进制文本，不将指数、十六进制或混杂字符转换成另一笔金额。 */
export function isDecimalInput(value, precision, signed = false) {
  const pattern = signed ? /^-?\d*(?:\.\d*)?$/ : /^\d*(?:\.\d*)?$/;
  if (!pattern.test(value)) return false;
  const fraction = value.split('.')[1];
  return precision === undefined || fraction === undefined || (precision > 0 && fraction.length <= precision);
}

/** 提交校验不接受空值、单独符号或未写完的小数点。具体上下限仍由业务规则校验。 */
export function isDecimalAmount(value, precision = 2, signed = false) {
  const text = String(value ?? '');
  return (signed ? /^-?\d+(?:\.\d+)?$/ : /^\d+(?:\.\d+)?$/).test(text)
    && isDecimalInput(text, precision, signed);
}

/** 文本金额显式标记；Arco数字组件统一覆盖，普通文本/密码/验证码不按名称猜测。 */
function inputOptions(input) {
  if (!input || input.tagName !== 'INPUT' || input.disabled || input.readOnly) return null;
  const owner = input.closest('[data-decimal-input]');
  if (owner) {
    const raw = owner.getAttribute('data-decimal-input');
    return {
      precision: raw === '' ? undefined : Number(raw),
      signed: owner.hasAttribute('data-decimal-signed'),
      separator: owner.getAttribute('data-decimal-separator'),
    };
  }
  if (input.closest('.arco-input-number')) {
    const min = input.getAttribute('aria-valuemin');
    return { precision: undefined, signed: min === null || Number(min) < 0 };
  }
  return null;
}

/** 捕获阶段先于Vue/Arco更新模型，覆盖键盘、粘贴、拖放、输入法及移动端input兜底。 */
export function installDecimalInputGuard(root = document) {
  const previous = new WeakMap();
  const valid = (value, options) => (options.separator ? value.split(options.separator) : [value])
    .every(part => isDecimalInput(part, options.precision, options.signed));
  const remember = (input, options) => {
    if (valid(input.value, options)) previous.set(input, input.value);
  };
  const candidate = (input, text) => {
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    return input.value.slice(0, start) + text + input.value.slice(end);
  };
  const before = (event) => {
    const input = event.target;
    const options = inputOptions(input);
    if (!options) return;
    remember(input, options);
    if (event.isComposing || event.inputType?.startsWith('delete')) return;
    let text;
    if (event.type === 'keydown') {
      if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) return;
      text = event.key;
    } else if (event.type === 'paste') {
      text = event.clipboardData?.getData('text/plain');
    } else if (event.type === 'drop') {
      text = event.dataTransfer?.getData('text/plain');
    } else {
      text = event.data;
    }
    if (text != null && !valid(candidate(input, text), options)) event.preventDefault();
  };
  const onInput = (event) => {
    const input = event.target;
    const options = inputOptions(input);
    if (!options) return;
    if (event.isComposing) {
      event.stopImmediatePropagation();
      return;
    }
    if (!valid(input.value, options)) {
      input.value = previous.get(input) ?? '';
    }
    remember(input, options);
  };
  const onFocus = (event) => {
    const options = inputOptions(event.target);
    if (options) remember(event.target, options);
  };
  const listeners = {
    focusin: onFocus,
    keydown: before,
    beforeinput: before,
    paste: before,
    drop: before,
    input: onInput,
    compositionend: onInput,
  };
  Object.entries(listeners).forEach(([name, handler]) => root.addEventListener(name, handler, true));
  return () => Object.entries(listeners).forEach(([name, handler]) => root.removeEventListener(name, handler, true));
}
